import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input
} from '@angular/core';
import { OSK_ICON_SIZE_BY_CONTROL_SIZE } from '../../core/utils/osk-size.utils';
import { OSKColorVariant, OSKIconName, OSKSize } from '../../types/osk-theme.types';
import { OSKIconComponent } from '../osk-icon/osk-icon.component';

export type OSKAvatarAppearance = 'solid' | 'soft' | 'outline';

export type OSKAvatarShape = 'circle' | 'rounded';

export type OSKAvatarSize = OSKSize | 'xl';

/**
 * Atom: <osk-avatar>
 * Supports centered initials ("MB"), icon badge, or image using Angular Signals.
 */
@Component({
  selector: 'osk-avatar',
  standalone: true,
  imports: [OSKIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-avatar.component.scss',
  template: `
    <span [class]="avatarClasses()">
      @if (src()) {
        <img class="osk-avatar__img" [src]="src()" [alt]="alt()" />
      } @else if (icon()) {
        <osk-icon [name]="icon()!" [size]="iconSize()"></osk-icon>
      } @else {
        <span class="osk-avatar__initials">{{ trimmedInitials() }}</span>
      }
    </span>
  `
})
export class OSKAvatarComponent {
  readonly color = input<OSKColorVariant>('primary');
  readonly appearance = input<OSKAvatarAppearance>('solid');
  readonly shape = input<OSKAvatarShape>('circle');
  readonly size = input<OSKAvatarSize>('md');
  readonly initials = input<string>('');
  readonly icon = input<OSKIconName | undefined>(undefined);
  readonly src = input<string | undefined>(undefined);
  readonly alt = input<string>('');

  protected readonly trimmedInitials = computed<string>(() => this.initials().trim());

  protected readonly avatarClasses = computed<string>(() =>
    [
      'osk-avatar',
      `osk-avatar--${this.color()}`,
      `osk-avatar--${this.appearance()}`,
      `osk-avatar--${this.shape()}`,
      `osk-avatar--${this.size()}`
    ].join(' ')
  );

  protected readonly iconSize = computed<number>(() => {
    const s = this.size();
    return s === 'xl' ? 32 : OSK_ICON_SIZE_BY_CONTROL_SIZE[s];
  });
}
